/* eslint-disable indent */
import checkSvg from '@src/main/assets/icons/check-red.svg';
import { Subtype, Type } from '@src/main/modules/parameters/domain/issues/category.interface';
import TypeForm from '@src/main/modules/parameters/ui/issues/components/TypeForm';
import { useCreateSubtype } from '@src/main/modules/parameters/ui/issues/hooks/create/useCreateSubtype';
import { useDeleteSubtype } from '@src/main/modules/parameters/ui/issues/hooks/delete/useDeleteSubtype';
import { useType } from '@src/main/modules/parameters/ui/issues/hooks/find/useType';
import { useUpdateSubtype } from '@src/main/modules/parameters/ui/issues/hooks/update/useUpdateSubtype';
import { getTypeSchema } from '@src/main/modules/parameters/ui/issues/typeSchema';
import { useToast } from '@src/main/shared/context/ToastContext';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTableSingle from '@src/main/shared/ui/components/table/AppTableSingle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

interface TableRow extends Subtype {
  id: string;
}

interface SubtypesProps {
  typeId: string;
  selectedType?: Type;
  selectedSubtype: Subtype | null;
}

const Subtypes: FC<SubtypesProps> = ({ typeId, selectedType, selectedSubtype }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: type, isLoading } = useType(typeId);
  const { mutateAsync: createSubtype } = useCreateSubtype();
  const { mutateAsync: updateSubtype } = useUpdateSubtype();
  const { mutateAsync: deleteSubtype } = useDeleteSubtype();

  const [subtypeToDelete, setSubtypeToDelete] = useState<Subtype | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(false);
  const [newEditSubtype, setNewEditSubtype] = useState<Partial<Subtype>>({
    title: '',
    visibleOnApp: true,
    types: selectedType ? [selectedType] : [],
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [true, true, false, true, false, false];

  const subtypesWithId: TableRow[] = (type?.subtypes ?? []).map((t) => ({
    id: t.subtypeId!,
    ...t,
    visibleOnAppDisplay: t.visibleOnApp ? (
      <img src={checkSvg} alt="Visible" width={16} height={16} />
    ) : null,
  }));

  const handleShowCreateModal = () => {
    setNewEditSubtype({
      title: '',
      visibleOnApp: false,
      types: selectedType ? [selectedType] : [],
    });
    setEditingSubtype(false);
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleEditSubtype = (row: TableRow) => {
    setNewEditSubtype({
      subtypeId: row.subtypeId,
      title: row.title,
      visibleOnApp: row.visibleOnApp,
      types: row.types,
    });
    setEditingSubtype(true);
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    const schema = getTypeSchema(t);

    try {
      schema.parse(newEditSubtype);

      const typeIdToUse = newEditSubtype.types?.[0]?.typeId ?? typeId;

      if (editingSubtype && newEditSubtype.subtypeId) {
        await updateSubtype({
          id: newEditSubtype.subtypeId!,
          data: {
            subtypeId: newEditSubtype.subtypeId!,
            title: newEditSubtype.title!,
            visibleOnApp: newEditSubtype.visibleOnApp ?? false,
            type: typeIdToUse,
          },
        });
        showSuccessMessage(toast, t('parameters.issues.subtypes.updatedSuccess'));
      } else {
        await createSubtype({
          title: newEditSubtype.title!,
          visibleOnApp: newEditSubtype.visibleOnApp ?? false,
          type: typeIdToUse,
        });
        showSuccessMessage(toast, t('parameters.issues.subtypes.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['type', typeId] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const confirmDelete = async () => {
    if (!subtypeToDelete) return;

    try {
      await deleteSubtype(subtypeToDelete.subtypeId!);
      await queryClient.invalidateQueries({ queryKey: ['type', typeId] });
      showSuccessMessage(toast, t('parameters.issues.subtypes.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setSubtypeToDelete(null);
    }
  };
  return (
    <div>
      <HeaderTitle title={t('parameters.issues.subtypes.title')} />

      <TableHeader
        onCreate={handleShowCreateModal}
        onDelete={() => setSubtypeToDelete(selectedSubtype || null)}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['type', typeId] });
          setIsRefreshing(false);
        }}
        visibility={toolbar}
      />

      <AppLoading isLoading={isLoading || isRefreshing}>
        <AppTableSingle
          headers={[
            { name: t('parameters.issues.titleField'), field: 'title' },
            { name: t('parameters.issues.visibleOnApp'), field: 'visibleOnAppDisplay' },
          ]}
          data={subtypesWithId}
          onEdit={handleEditSubtype}
          onDelete={(row) => setSubtypeToDelete({ ...row })}
        />
      </AppLoading>

      <AppDialog
        visible={isModalVisible}
        title={
          editingSubtype
            ? t('parameters.issues.subtypes.updateSubtype', { subtype: newEditSubtype.title })
            : t('parameters.issues.subtypes.createSubtype', {
                type: selectedType?.title,
              })
        }
        onClose={() => {
          setIsModalVisible(false);
          setValidationErrors({});
        }}
        disableButtons={isLoading}
        onConfirm={handleCreateEdit}
      >
        <TypeForm
          values={newEditSubtype}
          handleChange={(field, value) =>
            setNewEditSubtype((prev) => ({ ...prev, [field]: value }))
          }
          errors={validationErrors}
        />
      </AppDialog>

      <ConfirmDeleteDialog
        visible={!!subtypeToDelete}
        confirmDelete={confirmDelete}
        rejectDelete={() => setSubtypeToDelete(null)}
        message={t('parameters.issues.subtypes.confirmDelete', { subtype: subtypeToDelete?.title })}
      />
    </div>
  );
};

export default Subtypes;
